import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import {
  getCategoryTypeFromString,
  isEqlFieldCohorts,
  isEqlFieldCommon,
} from '@modules/common/utils';
import { getIconForIntegrationType } from '@modules/integrationsV2/utils';
import {
  AppsRounded,
  CategoryRounded,
  ChatRounded,
  FilterListRounded,
  FormatQuoteRounded,
  GroupWorkRounded,
  SummarizeRounded,
  TagRounded,
  TextFieldsRounded,
} from '@mui/icons-material';
import { Box, SvgIconTypeMap } from '@mui/material';
import { useTheme } from '@mui/system';
import { EqlFieldValueType, ObjectType } from 'generated/graphql';
import { EntitySentimentIcon } from '../EntitySentimentIcon';
import { FilterIcon } from '../FilterIcon/FilterIcon';
import { NegativeSentimentIcon } from '../NegativeSentimentIcon';
import { NeutralSentimentIcon } from '../NeutralSentimentIcon';
import { PositiveSentimentIcon } from '../PositiveSentimentIcon';
import { ReviewSentimentIcon } from '../ReviewSentimentIcon';
import { UnifiedFieldIcon } from '../UnifiedFieldIcon';

interface FieldIconProps {
  field?: string;
  iconURL?: string;
  color?: SvgIconTypeMap['props']['color'];
  fontSize?: SvgIconTypeMap['props']['fontSize'];
  value?: string;
  valueType?: EqlFieldValueType;
  objectType?: ObjectType;
}

export const FieldIcon = ({
  iconURL,
  field,
  color: colorProp,
  fontSize = 'small',
  value,
  valueType,
  objectType,
}: FieldIconProps) => {
  const theme = useTheme();

  if (objectType && objectType !== ObjectType.Feedback) {
    return getIconForIntegrationType({
      integrationType: objectType,
      fontSize,
    });
  }

  if (iconURL && !isEqlFieldCommon(field ?? '')) {
    return (
      <Box
        component="img"
        width="1rem"
        height="auto"
        src={iconURL}
        alt={field}
        loading="lazy"
      />
    );
  }

  if (!field) {
    return null;
  }

  const color = colorProp ?? theme.palette.text.secondary;

  switch (field) {
    case 'l1':
      return (
        <GroupWorkRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'entity':
      return (
        <TagRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'reason':
      return (
        <FormatQuoteRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'positive_sentiment':
      return <PositiveSentimentIcon color={color} fontSize={fontSize} />;
    case 'negative_sentiment':
      return <NegativeSentimentIcon color={color} />;
    case 'neutral_sentiment':
      return <NeutralSentimentIcon color={color} />;
    case 'search':
      return (
        <TextFieldsRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'source':
      return (
        <AppsRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'entitySentiment':
      return <EntitySentimentIcon color={color} fontSize={fontSize} />;
    case 'sentiment':
      return <ReviewSentimentIcon color={color} fontSize={fontSize} />;
    case 'category':
      if (value) {
        return <CategoryIcon category={getCategoryTypeFromString(value)} />;
      }
      return (
        <CategoryRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'summary':
      return (
        <SummarizeRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'feedbackRecords':
    case 'FEEDBACK':
      return (
        <ChatRounded
          sx={{
            color,
          }}
          fontSize={fontSize}
        />
      );
    case 'common':
    default:
      if (valueType === EqlFieldValueType.None) {
        return (
          <FilterListRounded
            sx={{
              color,
            }}
            fontSize={fontSize}
          />
        );
      }

      if (isEqlFieldCohorts(field)) {
        return (
          <SummarizeRounded
            sx={{
              color,
            }}
            fontSize={fontSize}
          />
        );
      }
      if (isEqlFieldCommon(field)) {
        return <UnifiedFieldIcon color={color} fontSize={fontSize} />;
      }

      return <FilterIcon color={color} fontSize={fontSize} />;
  }
};
